import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './providers/AuthProvider';
import { QueryProvider } from './providers/QueryProvider';
import { ViewportProvider } from './providers/ViewportProvider';
import { AuthGuard } from './components/auth/AuthGuard';

// Public Pages
import { LandingPage } from './pages/LandingPage';
import { SelectUserTypePage } from './pages/SelectUserTypePage';
import { AppPage } from './pages/AppPage';
import { PricingPage } from './pages/PricingPage';

// Auth Pages
import { LoginPage, RegisterPage, CallbackPage, DeviceSignInPage, WebSignInLinkPage, ScoutSignInPage } from './pages/auth';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';

// Dashboard Pages
import { DashboardLayout } from './layouts/DashboardLayout';
import {
  SubscriptionPage,
  OrdersPage,
  SettingsPage,
  InvoicesPage,
  DevicesPage,
  DownloadsPage,
  ScreenshotsPage,
  MyTimesheetPage,
  NavigationLibraryPage,
  NavigationLibraryRedirect,
  DriveChartsPage,
  SdCardPage,
  AnchorWatchPage,
  ShipsPapersPage,
  JoinVesselPage,
  TrackingPage,
} from './pages/dashboard';
import { BillingPlanPage } from './pages/dashboard/BillingPlanPage';
import { SharedDocumentPage } from './pages/SharedDocumentPage';
import { JoinRedirect } from './pages/JoinRedirect';
import { CheckoutPage } from './pages/dashboard/CheckoutPage';

// Crew paper requests (docs/wip/CREW_PAPER_REQUESTS.md)
import { RequestLinkPage } from './features/crewPapers/pages/RequestLinkPage';
import { MyPapersPage } from './features/crewPapers/pages/MyPapersPage';
import { CrewChecklistPage } from './features/crewPapers/pages/CrewChecklistPage';
import { CrewItemPage } from './features/crewPapers/pages/CrewItemPage';
import { CrewSharePage } from './features/crewPapers/pages/CrewSharePage';
import { CrewSharedPage } from './features/crewPapers/pages/CrewSharedPage';
import { DashboardHome } from './features/crewPapers/pages/DashboardHome';
import { RequestsListPage } from './features/crewPapers/skipper/RequestsListPage';
import { RequestFormPage } from './features/crewPapers/skipper/RequestFormPage';
import { RequestDetailPage } from './features/crewPapers/skipper/RequestDetailPage';
import { ExtendAskPage } from './features/crewPapers/skipper/ExtendAskPage';
import { ExtendAccessPage } from './features/crewPapers/pages/ExtendAccessPage';

// Admin Pages
import { AdminGuard } from './components/auth/AdminGuard';
import { AdminLayout } from './layouts/AdminLayout';
import {
  AdminDashboardPage,
  AdminFleetMapPage,
  AdminPackagesPage,
  AdminNavLibraryReportsPage,
  AdminPassageReportsPage,
  AdminUsersPage,
  AdminDevicesPage,
  AdminDeviceDetailPage,
  AdminFleetsPage,
  AdminFleetDetailPage,
  AdminSettingsPage,
  AdminEsriPage,
  AdminUsagePage,
  AdminTilesPage,
  AdminOnboardingPage,
  AdminApiKeysPage,
  AdminUpdatesPage,
  AdminSgReleasesPage,
  AdminAppLibraryPage,
  AdminLiveUpdatesPage,
  AdminWeatherPage,
  AdminRoutingPage,
  AdminRoutingNewRunPage,
  AdminDeparturePlannerPage,
  AdminFeedbackPage,
  AdminFeedbackDetailPage,
  AdminNeptuneDevicesPage,
  AdminInventoryPage,
  AdminInventoryDetailPage,
  AdminInventoryProductsPage,
  AdminInventoryBuildStationPage,
  AdminHardwareOrdersPage,
  AdminHardwareOrderDetailPage,
  AdminTimesheetsPage,
  AdminDepartmentsPage,
  AdminStaffPage,
  AdminMenuBuilderPage,
  AdminNmeaStudioPage,
} from './pages/admin';
import { DownloadPage } from './pages/DownloadPage';
import { TokensBoughtPage } from './pages/TokensBoughtPage';
import { AdminBillingAuditPage } from './pages/admin/AdminBillingAuditPage';

// OTA Releases (flagship admin area — own dark chrome)
import { AdminConsoleLayout } from './layouts/AdminConsoleLayout';
import {
  OtaOverviewPage,
  OtaChangelogPage,
  RegisterUpdatesPage,
  RegisterReviewPage,
  OtaBuildsPage,
  OtaStatusPage,
  OtaChannelsPage,
  OtaCoveragePage,
  OtaCheckLogPage,
  OtaAuditPage,
  OtaDeviceChannelsPage,
} from './pages/admin/releases';
import { HelpArticlesPage } from './pages/admin/help/HelpArticlesPage';
import { HelpEditorPage } from './pages/admin/help/HelpEditorPage';
import { HelpCheckPage } from './pages/admin/help/HelpCheckPage';
import { HelpImagesPage } from './pages/admin/help/HelpImagesPage';
import './styles/admin-console.css';

import { NotFoundPage } from './pages/NotFoundPage';
import { Toaster } from './components/ui/Toaster';
import { EnvironmentBanner } from './components/EnvironmentBanner';

// Embed Pages (public, no app shell)
import { EmbedPricingPage } from './pages/embed/EmbedPricingPage';
import { SgJoinPage } from './pages/sg/SgJoinPage';

function HomeRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

export function App() {
  return (
    <QueryProvider>
      <AuthProvider>
        <ViewportProvider>
          <BrowserRouter>
            <EnvironmentBanner />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/select-type" element={<SelectUserTypePage />} />
              <Route path="/app" element={<AppPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/download" element={<DownloadPage />} />
              {/* Stripe's return from a pack bought on the Scout: no sign-in (Tony, 3 Nov 2026). */}
              <Route path="/tokens/bought" element={<TokensBoughtPage />} />

              {/* Embed Routes (public, no shell) */}
              <Route path="/embed/pricing" element={<EmbedPricingPage />} />

              {/* Auth Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Scout onboarding magic-link landing (phone) — outside guards */}
              <Route path="/auth/device-signin" element={<DeviceSignInPage />} />
              <Route path="/auth/link" element={<WebSignInLinkPage />} />
              {/* A Scout signing its owner in, in its own web view. */}
              <Route path="/auth/scout" element={<ScoutSignInPage />} />
              <Route path="/s/:token" element={<SharedDocumentPage />} />
              {/* Vessel invitations emailed before 2026-09-25 link here; the page is /dashboard/join/:token. */}
              <Route path="/join/:token" element={<JoinRedirect />} />
              <Route path="/callback" element={<CallbackPage />} />

              {/* Crew paper requests. /r/<token> is the link in the request email:
                  public, it signs the crew member in with an emailed code. The
                  crew pages are mobile-first and sit outside the dashboard, so a
                  papers-only account never meets plan or device screens. */}
              <Route path="/r/:token" element={<RequestLinkPage />} />
              {/* The skipper's "ask to extend" link: public, signs in with the same code, then one tap. */}
              <Route path="/papers/extend/:token" element={<ExtendAccessPage />} />
              <Route path="/papers" element={<AuthGuard><MyPapersPage /></AuthGuard>} />
              <Route path="/papers/requests/:rid" element={<AuthGuard><CrewChecklistPage /></AuthGuard>} />
              <Route path="/papers/requests/:rid/items/:iid" element={<AuthGuard><CrewItemPage /></AuthGuard>} />
              <Route path="/papers/requests/:rid/share" element={<AuthGuard><CrewSharePage /></AuthGuard>} />
              <Route path="/papers/requests/:rid/shared" element={<AuthGuard><CrewSharedPage /></AuthGuard>} />

              {/* What the QR code in SG's Tools › Remote access opens (SCOUT_GATEWAY_REMOTE.md). */}

              <Route path="/sg/join" element={<AuthGuard><SgJoinPage /></AuthGuard>} />


              {/* Protected Dashboard Routes */}
              <Route
                path="/dashboard"
                element={
                  <AuthGuard>
                    <DashboardLayout />
                  </AuthGuard>
                }
              >
                {/* A papers-only account (crew from a request link) goes on to /papers. */}
                <Route index element={<DashboardHome />} />
                <Route path="billing" element={<BillingPlanPage />} />
                <Route path="devices" element={<DevicesPage />} />
                <Route path="downloads" element={<DownloadsPage />} />
                {/*
                  The Navigation Library moved to /library — the PassageWiki app
                  entered through the MyPolaris door (2026-09-11). This address
                  and every ?lib= deep link in the collection emails redirect
                  there. The old page stays one click away at .../classic until
                  we are sure of the new one; both are deleted together.
                */}
                <Route path="library" element={<NavigationLibraryRedirect />} />
                <Route path="library/classic" element={<NavigationLibraryPage />} />
                <Route path="drive-charts" element={<DriveChartsPage />} />
                <Route path="sd-card" element={<SdCardPage />} />
                <Route path="anchor-watch" element={<AnchorWatchPage />} />
                <Route path="papers" element={<ShipsPapersPage />} />
                {/* Skipper side of crew paper requests; the withdrawal email links to requests/:rid. */}
                <Route path="papers/requests" element={<RequestsListPage />} />
                <Route path="papers/requests/new" element={<RequestFormPage />} />
                <Route path="papers/requests/:rid" element={<RequestDetailPage />} />
                <Route path="papers/requests/:rid/edit" element={<RequestFormPage />} />
                {/* "Ask <name> to extend" in the access-ending email lands here. */}
                <Route path="papers/requests/:rid/extend" element={<ExtendAskPage />} />
                <Route path="join/:token" element={<JoinVesselPage />} />
                <Route path="screenshots" element={<ScreenshotsPage />} />
                <Route path="tracking" element={<TrackingPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="timesheet" element={<MyTimesheetPage />} />
                <Route path="subscription" element={<SubscriptionPage />} />
                <Route path="checkout" element={<CheckoutPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="profile" element={<SettingsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Register updates, one release: takes the whole window, so it sits
                  outside the console layout. */}
              <Route
                path="/admin/releases/register/:releaseId"
                element={
                  <AdminGuard>
                    <RegisterReviewPage />
                  </AdminGuard>
                }
              />

              {/* OTA Releases — own dark chrome, must come before /admin so the more
                  specific path wins. */}
              <Route
                path="/admin/releases"
                element={
                  <AdminGuard>
                    <AdminConsoleLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<OtaOverviewPage />} />
                <Route path="changelog" element={<OtaChangelogPage />} />
                <Route path="register" element={<RegisterUpdatesPage />} />
                <Route path="builds" element={<OtaBuildsPage />} />
                <Route path="status" element={<OtaStatusPage />} />
                <Route path="channels" element={<OtaChannelsPage />} />
                <Route path="coverage" element={<OtaCoveragePage />} />
                <Route path="log" element={<OtaCheckLogPage />} />
                <Route path="audit" element={<OtaAuditPage />} />
                <Route path="device-channels" element={<OtaDeviceChannelsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Help, one article's editor and its device check: each takes the whole window, like Register updates' review. */}
              <Route
                path="/admin/help/articles/:id"
                element={
                  <AdminGuard>
                    <HelpEditorPage />
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/help/articles/:id/check"
                element={
                  <AdminGuard>
                    <HelpCheckPage />
                  </AdminGuard>
                }
              />

              {/* Help (Scout help articles) — the same console chrome as OTA Releases. */}
              <Route
                path="/admin/help"
                element={
                  <AdminGuard>
                    <AdminConsoleLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<Navigate to="articles" replace />} />
                <Route path="articles" element={<HelpArticlesPage />} />
                <Route path="images" element={<HelpImagesPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* NMEA Studio — an instrument console, not a console page. It
                  takes the whole viewport, so it is mounted outside AdminLayout
                  and before /admin so the more specific path wins. */}
              <Route
                path="/admin/emulator"
                element={
                  <AdminGuard>
                    <AdminNmeaStudioPage />
                  </AdminGuard>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <AdminGuard>
                    <AdminLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<AdminDashboardPage />} />
                <Route path="packages" element={<AdminPackagesPage />} />
                <Route path="nav-library-reports" element={<AdminNavLibraryReportsPage />} />
                <Route path="paper-library-reports" element={<AdminNavLibraryReportsPage kind="papers" />} />
                <Route path="passage-reports" element={<AdminPassageReportsPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="devices" element={<AdminDevicesPage />} />
                <Route path="devices/:deviceId" element={<AdminDeviceDetailPage />} />
                <Route path="fleet-map" element={<AdminFleetMapPage />} />
                <Route path="fleets" element={<AdminFleetsPage />} />
                <Route path="fleets/:orgId" element={<AdminFleetDetailPage />} />
                <Route path="billing" element={<AdminBillingAuditPage />} />
                <Route path="esri" element={<AdminEsriPage />} />
                <Route path="usage" element={<AdminUsagePage />} />
                <Route path="tiles" element={<AdminTilesPage />} />
                <Route path="onboarding" element={<AdminOnboardingPage />} />
                <Route path="api-keys" element={<AdminApiKeysPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="data-portal" element={<AdminNeptuneDevicesPage />} />
                <Route path="updates" element={<AdminUpdatesPage />} />
                <Route path="sg-releases" element={<AdminSgReleasesPage />} />
                <Route path="app-library" element={<AdminAppLibraryPage />} />
                <Route path="updates/live" element={<AdminLiveUpdatesPage />} />
                <Route path="weather" element={<AdminWeatherPage />} />
                <Route path="routing" element={<AdminRoutingPage />} />
                <Route path="routing/new" element={<AdminRoutingNewRunPage />} />
                <Route path="departure-planner" element={<AdminDeparturePlannerPage />} />
                <Route path="feedback" element={<AdminFeedbackPage />} />
                <Route path="feedback/:id" element={<AdminFeedbackDetailPage />} />
                <Route path="timesheets" element={<AdminTimesheetsPage />} />
                <Route path="departments" element={<AdminDepartmentsPage />} />
                <Route path="staff" element={<AdminStaffPage />} />
                <Route path="menu-builder" element={<AdminMenuBuilderPage />} />
                <Route path="inventory" element={<AdminInventoryPage />} />
                <Route path="inventory/products" element={<AdminInventoryProductsPage />} />
                <Route path="build-station" element={<AdminInventoryBuildStationPage />} />
                <Route path="inventory/:itemId" element={<AdminInventoryDetailPage />} />
                <Route path="hardware-orders" element={<AdminHardwareOrdersPage />} />
                <Route path="hardware-orders/:orderId" element={<AdminHardwareOrderDetailPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* 404 - Page not found */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Toaster />
          </BrowserRouter>
        </ViewportProvider>
      </AuthProvider>
    </QueryProvider>
  );
}
